import { cn } from '@/lib/cn';
import { Check, Copy, X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Sticky bottom bar (action buttons). Stays visible while body scrolls. */
  footer?: ReactNode;
  /** 제목 줄 우측 (X 닫기 버튼 좌측) 슬롯 — 페이지 네비게이션 등 보조 컨트롤용. */
  titleActions?: ReactNode;
  /**
   * 값이 있으면 description 좌측에 작은 복사 버튼 렌더. 클릭 시 이 값을 clipboard 에 복사.
   * description prop 이 함께 있을 때만 의미 있음.
   */
  descriptionCopy?: string;
  /** Tailwind max-width — default `max-w-md`. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';
  /**
   * 다른 Modal 위에 쌓아야 하는 경우 'high' — z-50 대신 z-[60] 사용. 기본 'base' (z-50).
   * 한 화면에 2단계 이상은 권장하지 않음.
   */
  layer?: 'base' | 'high';
}

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-5xl',
  xxl: 'max-w-7xl',
  full: 'max-w-none',
};

const LAYER_CLASS: Record<NonNullable<ModalProps['layer']>, string> = {
  base: 'z-50',
  high: 'z-[60]',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  titleActions,
  descriptionCopy,
  size = 'md',
  layer = 'base',
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const [copied, setCopied] = useState(false);
  const handleCopyDescription = useCallback(async () => {
    if (!descriptionCopy) return;
    try {
      await navigator.clipboard.writeText(descriptionCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }, [descriptionCopy]);

  if (!open) return null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: <dialog> 태그는 open 상태 관리가 자체 prop 인데 여기서는 React state 로 토글
    <div
      role="dialog"
      aria-modal="true"
      className={cn('fixed inset-0 grid place-items-center px-4', LAYER_CLASS[layer])}
    >
      <button
        type="button"
        aria-label="닫기"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div
        className={cn(
          'relative flex w-full flex-col bg-surface text-foreground border border-border rounded-2xl shadow-xl',
          // full 사이즈는 명시적 height — 안쪽이 h-full / flex-1 로 비율 배치 시 collapse 방지.
          // 그 외 사이즈는 콘텐츠 만큼만 뜨도록 max-h.
          size === 'full' ? 'h-[calc(100dvh-2rem)]' : 'max-h-[calc(100dvh-2rem)]',
          SIZE_CLASS[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{title}</h2>
            {description ? (
              <div className="mt-1 flex items-start gap-1.5 text-sm text-foreground-muted">
                {descriptionCopy ? (
                  <button
                    type="button"
                    onClick={handleCopyDescription}
                    aria-label="설명 복사"
                    title={copied ? '복사됨' : '복사'}
                    className="shrink-0 mt-0.5 hover:text-foreground cursor-pointer"
                  >
                    {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  </button>
                ) : null}
                <p className="line-clamp-2" title={description}>
                  {description}
                </p>
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {titleActions}
            <button
              type="button"
              onClick={onClose}
              aria-label="닫기"
              className="text-foreground-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">{children}</div>
        {footer ? <div className="shrink-0 border-t border-border px-6 py-4">{footer}</div> : null}
      </div>
    </div>
  );
}
